import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { AuthShell, AuthHeading, AuthField, AuthAlert } from "../components/AuthShell";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const dest = useLocation().state?.from || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={dest} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate(dest);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell>
      <form onSubmit={submit} className="space-y-8" data-testid="login-form">
        <AuthHeading title="Sign in" subtitle="Owner and employees sign in here." />
        <div className="space-y-4">
          <AuthField id="email" label="Email" type="email" autoComplete="email" value={email} onChange={setEmail} testid="login-email-input" />
          <AuthField id="password" label="Password" type="password" autoComplete="current-password" value={password} onChange={setPassword} testid="login-password-input"
            right={<Link to="/forgot-password" className="text-xs text-amber-400 hover:underline" data-testid="login-forgot-password-link">Forgot password?</Link>} />
          {error && <AuthAlert testid="login-error">{error}</AuthAlert>}
        </div>
        <Button type="submit" disabled={busy} className="h-11 w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="login-submit-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4" /></>}
        </Button>
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <ShieldCheck className="h-4 w-4 text-slate-500" /> New employee?
          <Link to="/signup" className="text-amber-400 hover:underline font-medium" data-testid="login-signup-link">Request an account</Link>
        </div>
      </form>
    </AuthShell>
  );
}
